<template>
  <ListGroup
    v-if="valuations?.length"
    class="mb-4"
  >
    <ListGroupItem
      v-for="valuation in valuations"
      :key="valuation.id"
      :route="
        valuation.type == 'part-exchange'
          ? '/vehicles/valuations/' + valuation.id
          : '/vehicles/reauction/' + valuation.id
      "
    >
      <div class="flex justify-between items-center">
        <div class="flex gap-4 items-center">
          <Icon
            type="circle-dashed"
            class="text-xl text-info"
          />
          <span>{{ valuationStatusTitle(valuation).title }}</span>
          <Chip size="sm">{{ valuation.type == 'part-exchange' ? 'Part Exchange' : 'Sell' }}</Chip>
        </div>
        <div
          v-if="valuation.updatedAt"
          class="text-sm text-secondary"
        >
          Updated: {{ dayjs(valuation.updatedAt).fromNow() }}
        </div>
        <div
          v-else
          class="text-sm text-secondary"
        >
          Created: {{ dayjs(valuation.createdAt).fromNow() }}
        </div>
      </div>
    </ListGroupItem>
  </ListGroup>

  <div class="w-full flex">
    <Button
      :label="$t('ui.actions.new')"
      icon="plus"
      @click="newValuationModalIsOpen = true"
    />
  </div>

  <teleport to="#modals-container">
    <Modal
      title="New Valuation"
      description="Create a new valuation for this vehicle"
      icon="car"
      :is-open="newValuationModalIsOpen"
      ok-text="Create"
      close-text="Cancel"
      :buttons="newValuation.reason ? ['close', 'ok'] : []"
      :has-unsaved-changes="newValuationHasUnsavedChanges"
      show-loader-on-confirm
      :is-confirming="newValuationApiRequest.isLoading.value"
      :disable-close="newValuationApiRequest.isLoading.value"
      min-width="400"
      @close-modal="((newValuationModalIsOpen = false), (newValuationHasUnsavedChanges = false))"
      @ok-modal="submitNewValuation()"
    >
      <FormGroup class="w-[280px]">
        <RadioBoxed
          v-model="newValuation.reason"
          :options="reasonOptions"
        />
      </FormGroup>
      <div v-if="newValuation.reason">
        <div class="grid grid-cols-2 gap-4 mb-4">
          <FormGroup
            label="Has Service History"
            no-margin
          >
            <RadioTabbed
              v-model="newValuation.hasServiceBook"
              :options="serviceOptions"
              full-width
            />
          </FormGroup>
          <FormGroup
            label="Full Franchised Service"
            no-margin
          >
            <RadioTabbed
              v-model="newValuation.hasFullFranchisedService"
              :options="serviceOptions"
              full-width
            />
          </FormGroup>
          <FormGroup
            label="Current Mileage"
            no-margin
          >
            <Input
              v-model="newValuation.latestMileage"
              icon="gauge"
              :has-error="valuationV$.latestMileage.$error"
            />
            <InputError :has-error="valuationV$.latestMileage.$error">
              {{ valuationV$.latestMileage.$errors[0].$message }}
            </InputError>
          </FormGroup>
          <FormGroup
            label="Last Service Mileage"
            :is-required="valuationV$.lastServiceMileage.required"
            no-margin
          >
            <Input
              v-model="newValuation.lastServiceMileage"
              :has-error="valuationV$.lastServiceMileage.$error"
              icon="gauge"
            />
            <InputError :has-error="valuationV$.lastServiceMileage.$error">
              {{ valuationV$.lastServiceMileage.$errors[0].$message }}
            </InputError>
          </FormGroup>
          <FormGroup
            label="Last Service Date"
            no-margin
          >
            <DatePicker v-model="newValuation.lastServiceDate" />
          </FormGroup>
        </div>

        <div v-if="newValuation.reason === 'sell'">
          <div class="grid grid-cols-2 gap-4">
            <FormGroup label="Listing Coverage">
              <RadioTabbed
                v-model="newValuation.listingReach"
                :options="listingReachOptions"
                :has-error="valuationV$.listingReach.$error"
                full-width
              />
              <InputError :has-error="valuationV$.listingReach.$error">
                {{ valuationV$.listingReach.$errors[0].$message }}
              </InputError>
            </FormGroup>
            <FormGroup
              label="Current Mileage"
              no-margin
            >
              <Input
                v-model="newValuation.latestMileage"
                icon="gauge"
                :has-error="valuationV$.latestMileage.$error"
              />
              <InputError :has-error="valuationV$.latestMileage.$error">
                {{ valuationV$.latestMileage.$errors[0].$message }}
              </InputError>
            </FormGroup>
          </div>
          <Checkbox
            v-model="newValuation.settlementConsent"
            label="Consent given by customer for any dealership to request settlement figure"
          />
        </div>

        <FormGroup>
          <Checkbox
            v-model="newValuation.requestImagesFromCustomer"
            :readonly="authStore.roles.includes(2) || !canSendAutomatedComms"
            class="mt-4"
            label="Request images from customer"
          />
        </FormGroup>
        <Alert
          v-if="!canSendAutomatedComms"
          severity="warning"
          :title="
            !callCenterStore.selectedCallCenter
              ? 'No call centre selected - vehicle images can\'t be requested from customer'
              : !isCustomerEmailValid
                ? 'Customer email is invalid - vehicle images can\'t be requested from customer'
                : 'Automated emails cannot be sent from this dealership - vehicle images can\'t be requested from customer'
          "
          :show-close-button="false"
          :show-dismiss-button="false"
        />
      </div>
    </Modal>
  </teleport>
</template>

<script setup>
// vue
import { ref } from 'vue'
// components
import Alert from '@/components/alert/Alert.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Modal from '@/components/modal/Modal.vue'
import RadioBoxed from '@/components/forms/RadioBoxed.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import Icon from '@/components/icon/Icon.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
// composables
import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useCallCenterStore } from '@/stores/CallCenterStore'
import { useAccessStore } from '@/stores/AccessStore'
// utilities
import { alertToast } from '@/utilities/notification'
// validators
import useVuelidate from '@vuelidate/core'
import { required, numeric, minValue } from '@vuelidate/validators'
// libraries
import dayjs from 'dayjs'
// utilities
import { formatNumber } from '@/utilities/number'

const props = defineProps({
  agreementData: {
    type: Object,
    default: null
  }
})
const emit = defineEmits(['onSubmit'])
const newValuationModalIsOpen = ref(false)
const newValuationHasUnsavedChanges = ref(false)

const valuations = ref([])

const valuationsApiRequest = useApiRequest()
const newValuationApiRequest = useApiRequest()
const updateAgreementApiRequest = useApiRequest()

const authStore = useAuthStore()
const callCenterStore = useCallCenterStore()
const accessStore = useAccessStore()

valuationsApiRequest
  .send({
    endpoint: '/v1/valuations?agreementId=' + props.agreementData.id
  })
  .then(response => {
    valuations.value = response.data
  })

function valuationStatusTitle(valuation) {
  if (valuation.isDeclined) {
    return { title: 'Declined', subTitle: 'by ' + valuation.declinedByName, icon: 'circle-xmark' }
  }
  if (valuation.valuation) {
    return {
      title: 'Valued at ' + formatNumber(valuation.valuation, 'currency', 0, valuation.currencyCode),
      subTitle: 'by ' + valuation.valuedByName,
      icon: 'circle-check'
    }
  }
  if (valuation.canBeValued) {
    return { title: 'Awaiting Valuation', subTitle: null, icon: 'circle-dashed' }
  } else {
    return {
      title: 'Awaiting Images from Customer',
      subTitle: null,
      icon: 'circle-three-quarters-stroke'
    }
  }
}

const isCustomerEmailValid = ref(false)
const canSendAutomatedComms = ref(false)
const emailValidationRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
if (
  props.agreementData.customer.contact.email &&
  emailValidationRegex.test(props.agreementData.customer.contact.email)
) {
  isCustomerEmailValid.value = true
}

if (
  props.agreementData.dealership.automatedCommsConfig.isPrimary &&
  callCenterStore.selectedCallCenter &&
  isCustomerEmailValid.value
) {
  canSendAutomatedComms.value = true
}

const newValuationDefault = {
  agreementId: props.agreementData.id,
  vrm: props.agreementData.vehicle.vrm,
  countryCode: props.agreementData.countryCode,
  currencyCode: props.agreementData.currency,
  reason: null,
  latestMileage: props.agreementData.odometer.current || null,
  hasServiceBook: null,
  hasFullFranchisedService: null,
  lastServiceDate: null,
  lastServiceMileage: null,
  requestImagesFromCustomer: canSendAutomatedComms.value,
  settlementConsent: false,
  listingReach: 'group'
}
const newValuation = ref({ ...newValuationDefault })

// Check that this dealership has the ReAuction feature enabled
const dealership = accessStore.accessAllowed.dealerships.find(
  dealership => dealership.id == props.agreementData.dealership.id
)

const reasonOptions = [
  { value: 'part-exchange', label: 'Part Exchange', icon: 'right-left' },
  {
    value: 'sell',
    label: 'Sell',
    icon: 'badge-dollar',
    disabled: !dealership?.features?.auction,
    disabledTooltip: 'ReAuction not enabled'
  }
]

const serviceOptions = [
  { label: 'Yes', value: true, description: null },
  { label: 'No', value: false, description: null },
  { label: 'Unknown', value: null, description: null }
]

const listingReachOptions = [
  { label: 'Dealership', value: 'dealership', description: null },
  { label: 'Group', value: 'group', description: null },
  { label: 'National', value: 'national', description: null }
]

const vuelidateRules = {
  lastServiceMileage: {
    numeric,
    required,
    minValue: minValue(1)
  },
  latestMileage: {
    numeric,
    required
  },
  listingReach: {
    required
  }
}

const valuationV$ = useVuelidate(vuelidateRules, newValuation)
const isFormCorrect = ref(null)

async function submitNewValuation() {
  isFormCorrect.value = await valuationV$.value.$validate()
  if (isFormCorrect.value) {
    // Make sure lastServiceDate is null not an empty string
    if (newValuation.value.lastServiceDate == '') {
      newValuation.value.lastServiceDate = null
    }

    newValuationApiRequest
      .send({
        endpoint: '/v1/valuations',
        method: 'POST',
        data: newValuation.value
      })
      .then(response => {
        if (!response.success) {
          alertToast('Failed to Create', response.data?.message, 'error')
          return
        }
        alertToast('Valuation created', null, 'success')
        newValuationModalIsOpen.value = false
        newValuationHasUnsavedChanges.value = false
        newValuation.value = { ...newValuationDefault }
        // re-fetch valuations
        valuationsApiRequest
          .send({
            endpoint: '/v1/valuations?agreementId=' + props.agreementData.id
          })
          .then(response => {
            valuations.value = response.data
          })
      })

    updateAgreementApiRequest.send({
      endpoint: '/v1/agreements/' + props.agreementData.id,
      method: 'PATCH',
      data: { odometerCurrent: newValuation.value.latestMileage }
    })

    emit('onSubmit', newValuation.value)
  }
}
</script>

<style></style>
